import React from 'react';
import MetricLoader from '../MetricLoader';
import ComparisonMetric from '../ComparisonMetric';
import moment from 'moment-timezone';
import {useProjectsContext} from "@/pages/dashboard/ProjectsContext";
import useCoreProjectsMetrics from "@/hooks/useCoreProjectsMetrics.js";
import _ from "lodash";

/**
 * StudentsYear Component
 *
 * Displays a comparison metric showing the number of students impacted by completed projects
 * in the current school year compared to the previous school year.
 *
 * @param {Object} props - Component props
 * @param {Object} [props.network] - Network object containing network id
 * @param {string} props.schoolStart - Start date of the current school year
 * @param {boolean} [props.titleOnly] - Flag to show only title without comparison data
 * @returns {JSX.Element} - A comparison metric or loading indicator
 */
export default function StudentsYear(props) {
  const {network, schoolStart, titleOnly} = props;
  const {filteredProjects, selectedSiteIds, isLoading} = useProjectsContext();

  // Calculate the start date for the previous school year (one year before current)
  const previousSchoolStart = moment(schoolStart).subtract(1, 'year').toISOString();

  // Fetch projects from the previous school year for comparison
  // titleOnly is set to true for the "All years" path. Do not enable the previous counts for that path.
  const previousProjects = useCoreProjectsMetrics({
    networkId: network?.id,
    startDate: titleOnly ? undefined : previousSchoolStart,
    endDate: schoolStart,
    pageSize: 1000,
  }, {enabled: titleOnly !== true});

  // Show loading state while data is being fetched
  if ((titleOnly !== true && previousProjects.isLoading) || isLoading) {
    return (
      <MetricLoader
        name="Students Impacted"
      />
    );
  }

  /**
   * Calculate total students impacted in the previous school year
   *
   * Filters:
   * - Only completed projects
   * - Only projects with valid student counts (>= 0, not null)
   * - Only projects from selected sites (if site filtering is active)
   */
  const previousCount = _.reduce(previousProjects.data, function (result, project) {
    if (!project.completed) {
      return result;
    }

    if (project.students < 0 || project.students === null) {
      return result;
    }

    const siteId = project.siteId;
    if (selectedSiteIds.length > 0 && !selectedSiteIds.includes(siteId)) {
      return result;
    }
    return result + project.students;
  }, 0);

  /**
   * Calculate total students impacted in the current school year
   *
   * Filters:
   * - Only completed projects
   * - Uses 0 as default for projects without student data
   */
  const newCount = _.reduce(filteredProjects, function (result, event) {
    if (!event.completed) {
      return result;
    }

    return result + (event.students || 0);
  }, 0);

  // Render comparison metric showing current vs previous year
  return (
    <ComparisonMetric
      name="Students Impacted"
      value={newCount}
      previousValue={previousCount}
      titleOnly={titleOnly}
    />
  );
}
